import React, { useEffect, useState } from 'react';
import Button from '../ui/Button';

const MobileIngredientsPanel = ({
  ingredientList,
  loadingIngredients,
  handleCopyIngredients,
  handleClearIngredients,
  copyMessage,
  selectedRecipesCount,
  realIngredientCount,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  
  // Automatically expand panel when ingredients are loaded
  useEffect(() => {
    if (ingredientList.length > 0 && !loadingIngredients) {
      setIsExpanded(true);
    }
  }, [ingredientList, loadingIngredients]);

  // Automatically collapse panel when ingredients are cleared
  useEffect(() => {
    if (ingredientList.length === 0 && selectedRecipesCount === 0 && !loadingIngredients && isExpanded) {
      setIsExpanded(false);
    }
  }, [ingredientList.length, selectedRecipesCount, loadingIngredients, isExpanded]);

  // Don't hide the panel immediately - show it if there are selected recipes or if loading
  if (selectedRecipesCount === 0 && !loadingIngredients && ingredientList.length === 0) {
    return null;
  }

  return (
    <>
      {/* Mobile floating badge */}
      <div className="fixed bottom-24 right-4 lg:hidden z-50">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="ingredients-badge bg-green-600/95 backdrop-blur-sm text-white rounded-full py-2 px-3 shadow-lg flex items-center justify-center space-x-2 hover:bg-green-700 transition-colors will-change-transform"
          style={{ transform: 'translateZ(0)' }}
        >
          <span className="text-sm font-medium">{selectedRecipesCount}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
            <path
              fillRule="evenodd"
              d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
              clipRule="evenodd"
            />
          </svg>
          <span className="bg-white text-green-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
            {loadingIngredients ? '...' : realIngredientCount}
          </span>
        </button>
      </div>

      {/* Mobile expandable panel */}
      <div
        className={`fixed inset-x-0 bottom-0 lg:hidden mobile-ingredients-panel transform transition-all duration-300 ease-in-out will-change-transform ${
          isExpanded ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{
          maxHeight: 'calc(85vh)',
          minHeight: '200px',
          paddingBottom: 'env(safe-area-inset-bottom)',
          transform: `translateZ(0) translateY(${isExpanded ? '0' : '100%'})`,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div className="bg-white/95 backdrop-blur-sm rounded-t-xl shadow-lg h-full flex flex-col max-h-full">
          {/* Handle for dragging */}
          <div
            className="w-12 h-1 bg-gray-300 rounded-full mx-auto my-3 cursor-grab flex-shrink-0"
            onClick={() => setIsExpanded(!isExpanded)}
          />

          {/* Header - Fixed */}
          <div className="px-4 py-2 border-b border-gray-200 bg-white/95 backdrop-blur-sm flex-shrink-0">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-semibold text-gray-800">
                  Ingredients
                </h2>
                <span className="text-sm text-gray-500">
                  ({selectedRecipesCount}{' '}
                  {selectedRecipesCount === 1 ? 'recipe' : 'recipes'})
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="text"
                  onClick={handleCopyIngredients}
                  disabled={loadingIngredients}
                  className={`flex-1 text-green-600 font-medium text-xs ${copyMessage ? 'bg-green-50' : 'hover:bg-green-50'} px-3 transition-colors duration-200`}
                  size="sm"
                >
                  {copyMessage || 'Copy'}
                </Button>
                <Button
                  variant="text"
                  onClick={handleClearIngredients}
                  disabled={loadingIngredients || ingredientList.length === 0}
                  className="flex-1 text-red-600 font-medium text-xs hover:bg-red-50 px-3 transition-colors duration-200"
                  size="sm"
                >
                  Clear
                </Button>
              </div>
            </div>
          </div>

          {/* Ingredients List - Scrollable */}
          <div className="flex-1 overflow-y-auto overscroll-contain min-h-0">
            <div className="p-4 space-y-2">
              {loadingIngredients ? (
                <div className="animate-pulse space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-6 bg-gray-200 rounded w-3/4" />
                  ))}
                </div>
              ) : ingredientList.length > 0 ? (
                ingredientList.map((ingredient, index) => (
                  <div key={index} className="flex items-center py-1">
                    <span className="text-gray-800 mr-2 leading-none">•</span>
                    <span className="text-gray-900">{ingredient}</span>
                  </div>
                ))
              ) : selectedRecipesCount > 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-500 text-sm">
                    No ingredients found for selected {selectedRecipesCount === 1 ? 'recipe' : 'recipes'}.
                  </p>
                  <p className="text-gray-400 text-xs mt-2">
                    The selected {selectedRecipesCount === 1 ? 'recipe' : 'recipes'} may not have ingredients data.
                  </p>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500 text-sm">
                    Select recipes to view their combined ingredients.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default MobileIngredientsPanel
